/**
 * Pure helpers for FunctionSourceEditor — kept React-free so the math is
 * unit-testable without rendering or Monaco. Phase 1 PR-F (2026-05-13).
 */
import type {
  FunctionEditorLanguage,
  FunctionEditorVariant,
} from './FunctionSourceEditor.types';

/**
 * Maps caller-facing language hints to Monaco's language ids.
 *
 *   typescript / ts → 'typescript'
 *   python / py     → 'python'
 *   native          → 'plaintext'  (pointer to compiled-in module; not source)
 *
 * Centralizing this fixes a footgun where three different call sites
 * each had their own tiny mapping function: `runtime === 'ts' ? 'typescript'
 * : 'python'` in NewFunctionSheet, `runtimeToMonacoLang()` in the sysadmin
 * detail dialog, and inline `language === 'typescript' ? 'typescript' :
 * 'python'` in the tenant Sheet. All three had subtle differences (the
 * tenant one couldn't handle 'native'; the inline one couldn't handle
 * canonical 'ts'/'py' codes).
 */
export function functionLanguageToMonacoLang(
  lang: FunctionEditorLanguage,
): string {
  switch (lang) {
    case 'ts':
    case 'typescript':
      return 'typescript';
    case 'py':
    case 'python':
      return 'python';
    case 'native':
      return 'plaintext';
    default: {
      // Exhaustive narrowing — unreachable at runtime, but TS-checked.
      const _exhaustive: never = lang;
      void _exhaustive;
      return 'plaintext';
    }
  }
}

/**
 * Variant-specific Monaco option presets. Each entry is a faithful capture
 * of the call site's pre-refactor options block — visual rendering MUST
 * remain byte-identical. Changes to these presets are a redesign, not a
 * refactor, and would need a separate PR + screenshot diff review.
 *
 * Why a discriminator instead of a smaller set of booleans:
 *   - tenant 'tenant-sheet' has 14 options vs sysadmin's 6 — collapsing
 *     them into individual props would inflate the API surface enough that
 *     callers stop reading it and start passing wrong combinations.
 *   - A named variant makes the historical lineage of each preset explicit:
 *     anyone reading routes/connections/functions.tsx can grep
 *     'tenant-sheet' and see exactly what knobs that surface controls.
 */
export type FunctionEditorOptions = {
  readOnly: boolean;
  minimap: { enabled: boolean };
  fontSize: number;
  lineNumbers: 'on';
  scrollBeyondLastLine: false;
  wordWrap: 'on';
  tabSize?: number;
  padding?: { top: number; bottom: number };
  bracketPairColorization?: { enabled: boolean };
  guides?: { bracketPairs: boolean; indentation: boolean };
  folding?: boolean;
  renderLineHighlight?: 'all';
  smoothScrolling?: boolean;
  cursorBlinking?: 'smooth';
  cursorSmoothCaretAnimation?: 'on';
};

export interface BuildOptionsArgs {
  variant: FunctionEditorVariant;
  language: FunctionEditorLanguage;
  readOnly: boolean;
  /** Only consulted on 'tenant-sheet' to toggle the minimap. */
  fullscreen: boolean;
}

export function buildFunctionEditorOptions(
  args: BuildOptionsArgs,
): FunctionEditorOptions {
  const { variant, language, readOnly, fullscreen } = args;

  // Tenant Sheet — the richest variant. Mirrors the inline options block
  // that used to live in routes/connections/functions.tsx pre-refactor.
  if (variant === 'tenant-sheet') {
    return {
      readOnly,
      minimap: { enabled: fullscreen },
      fontSize: 13,
      lineNumbers: 'on',
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      tabSize: language === 'python' || language === 'py' ? 4 : 2,
      padding: { top: 12, bottom: 12 },
      bracketPairColorization: { enabled: true },
      guides: { bracketPairs: true, indentation: true },
      folding: true,
      renderLineHighlight: 'all',
      smoothScrolling: true,
      cursorBlinking: 'smooth',
      cursorSmoothCaretAnimation: 'on',
    };
  }

  // Sysadmin Source tab (Detail dialog) + NewFunctionSheet — both
  // used the same 6-option preset historically. Fewer flourishes than
  // the tenant Sheet: minimap off, fontSize 12, no padding, no guides.
  return {
    readOnly,
    minimap: { enabled: false },
    fontSize: 12,
    lineNumbers: 'on',
    scrollBeyondLastLine: false,
    wordWrap: 'on',
  };
}

/**
 * Resolves a Monaco theme name from the caller's hint + variant default.
 *
 *   - 'tenant-sheet'         : caller controls (passes useTheme().mode)
 *                              undefined falls back to 'vs-dark' for safety.
 *   - 'sysadmin-source-tab'  : hard-coded 'vs-dark' historically
 *   - 'sysadmin-create-sheet': hard-coded 'vs-dark' historically
 *
 * Monaco's built-in theme ids: 'vs' (light), 'vs-dark' (dark), 'hc-black'.
 * We map 'light' → 'vs' and 'dark' → 'vs-dark' to keep the API typed in
 * terms the rest of the design system already uses (matches ThemeProvider).
 */
export function resolveMonacoTheme(
  variant: FunctionEditorVariant,
  theme: 'light' | 'dark' | undefined,
): string {
  if (variant === 'sysadmin-source-tab' || variant === 'sysadmin-create-sheet') {
    return 'vs-dark';
  }
  // tenant-sheet — caller controls
  if (theme === 'light') return 'vs';
  if (theme === 'dark') return 'vs-dark';
  return 'vs-dark';
}

/**
 * Default heights per variant — match the pre-refactor inline styles so
 * the surrounding layout doesn't shift.
 */
export function variantDefaultHeight(variant: FunctionEditorVariant): string {
  switch (variant) {
    case 'tenant-sheet':
      // The tenant Sheet wraps the editor in a flex-1 min-h-0 container,
      // so 100% expands to fill it.
      return '100%';
    case 'sysadmin-source-tab':
      // Pre-refactor: 420px fixed height inside the Source tab.
      return '420px';
    case 'sysadmin-create-sheet':
      // Pre-refactor: 300px fixed height inside NewFunctionSheet.
      return '300px';
    default: {
      const _exhaustive: never = variant;
      void _exhaustive;
      return '300px';
    }
  }
}

/**
 * Default container wrapper class per variant. The sysadmin call sites
 * historically wrapped the <Editor/> in `<div className="border rounded-
 * md overflow-hidden h-[...]"/>`, while the tenant Sheet did NOT wrap and
 * let the editor expand into a `flex-1 min-h-0` parent. Returning '' for
 * tenant preserves that.
 */
export function variantDefaultContainerClass(
  variant: FunctionEditorVariant,
  height: string,
): string {
  if (variant === 'tenant-sheet') {
    return '';
  }
  // sysadmin variants — `border rounded-md overflow-hidden` + fixed height.
  return `border rounded-md overflow-hidden`;
  // NOTE: height is set on the <Editor/> itself (and via the container's
  // inline style), which matches what the call sites did pre-refactor.
  // We deliberately do NOT bake a Tailwind h-[N] class here because
  // Tailwind JIT can't statically analyze a runtime string.
  void height;
}
